import { CheckIcon, PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { ActorRefFrom } from 'xstate';
import type { profileFormMachine } from '../profileForm.machine';
import { ContactCard } from './ContactCard';
import { PhoneColgroup } from './contactTableLayout';

const TAGS = CT.profile.contactInfo.phone.table;
const CARD_TAGS = CT.profile.contactInfo.phone.card;
interface AddressTableProps {
  actor: ActorRefFrom<typeof profileFormMachine>;
  onDelete: (index: number) => void;
}

export const PhoneTable = (props: AddressTableProps) => {
  const { actor, onDelete } = props;
  const phoneNumbers = useSelector(actor, (state) => state.context.phoneNumbers);
  const handleOpen = () => actor.send({ type: 'ON_ADD_PHONE_NUMBER' });
  const handleEdit = (index: number) => actor.send({ type: 'ON_EDIT_PHONE_NUMBER', index });
  const handleDelete = (index: number) => onDelete(index);
  return (
    <>
      <div className="flex items-center gap-2">
        <div className="flex-auto min-w-0">
          <h2 className="font-semibold text-xl text-content-primary truncate">Phone Numbers</h2>
        </div>
        <div className="flex-none">
          <button
            type="button"
            aria-label="Add phone number"
            title="Add phone number"
            data-cy={TAGS.addPhoneNum}
            onClick={handleOpen}
            className="flex items-center justify-center rounded-lg p-1.5 text-primary-50 border border-primary-t-700 hover:bg-primary-t-900 hover:border-primary-t-600 transition-colors cursor-pointer"
          >
            <PlusCircleIcon className="size-5" aria-hidden="true" />
          </button>
        </div>
      </div>
      {/* [MCV-1332] Desktop: the aligned table. Hidden below md, where a phone
          can't fit the columns without horizontal scroll. */}
      <div className="mt-2 hidden md:block flow-root border-2 border-gray-2 rounded-md p-3">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full table-fixed divide-y divide-gray-2">
              <PhoneColgroup />
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-content-secondary sm:pl-0"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-content-secondary"
                  >
                    Country Code
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-content-secondary"
                  >
                    Phone Number
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-content-secondary"
                  >
                    Primary
                  </th>

                  <th scope="col" className="relative py-3.5 pr-4 pl-3 sm:pr-0">
                    <span className="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-2">
                {phoneNumbers.map((number, index) => (
                  <tr
                    key={`${number.phoneType.value}-${number.phoneNum}`}
                    data-cy={TAGS.phoneRow + index}
                  >
                    <td
                      data-cy={TAGS.phoneTypeRow + index}
                      title={number.phoneType.label}
                      className="py-4 pr-3 pl-4 text-sm font-medium truncate text-content-secondary sm:pl-0"
                    >
                      {number.phoneType.label}
                    </td>
                    <td
                      data-cy={TAGS.phoneCountryCode + index}
                      title={number.countryCode.value}
                      className="px-3 py-4 text-sm truncate text-content-secondary"
                    >
                      {number.countryCode.value}
                    </td>
                    <td
                      data-cy={TAGS.phoneNumberRow + index}
                      title={number.phoneNum}
                      className="px-3 py-4 text-sm truncate text-content-secondary"
                    >
                      {number.phoneNum}
                    </td>
                    <td className="px-3 py-4 text-sm whitespace-nowrap">
                      {number.primaryFlag && <CheckIcon className="size-6" />}
                    </td>

                    <td className="relative py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-0">
                      <button
                        type="button"
                        className="text-primary-50 hover:text-primary-200 cursor-pointer"
                        onClick={() => handleEdit(index)}
                      >
                        Edit<span className="sr-only">, {number.phoneType.label}</span>
                      </button>
                      <button
                        type="button"
                        className="text-error hover:text-error-100 cursor-pointer ml-3"
                        onClick={() => handleDelete(index)}
                      >
                        <TrashIcon className="size-4 inline" />
                        <span className="sr-only">Delete {number.phoneType.label}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* [MCV-1332] Mobile: stacked cards instead of a horizontally-scrolling table. */}
      <ul className="md:hidden mt-2 flex flex-col gap-3">
        {phoneNumbers.map((number, index) => (
          <ContactCard
            key={`${number.phoneType.value}-${number.phoneNum}`}
            dataCy={CARD_TAGS.row + index}
            title={number.phoneType.label}
            isPrimary={number.primaryFlag}
            fields={[
              {
                label: 'Country Code',
                value: number.countryCode.value,
                title: number.countryCode.value,
              },
              { label: 'Phone Number', value: number.phoneNum, title: number.phoneNum },
            ]}
            onEdit={() => handleEdit(index)}
            onDelete={() => handleDelete(index)}
            label={number.phoneType.label}
          />
        ))}
      </ul>
    </>
  );
};
